import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, IconButton } from '@mui/material';
import { EditIcon, SendIcon } from 'lucide-react';
interface Transaction {
  id: string;
  senderName: string;
  receiverName: string;
  amount: number;
  currency: string;
  payoutAmount: number;
  payoutCurrency: string;
  date: string;
  status: string;
  rejectionReason?: string;
  createdAt?: string;
  branchCode?: string;
  agentCode?: string;
  mto?: string;
}
const RejectedTransactionsApprovalPage: React.FC = () => {
  const [rejectedTransactions, setRejectedTransactions] = useState<Transaction[]>([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState<Transaction | null>(null);
  const [editedData, setEditedData] = useState<any>({});
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };
  const loadRejectedTransactions = () => {
    const stored = localStorage.getItem('initiatedTransactions');
    if (stored) {
      const allTransactions = JSON.parse(stored);
      const rejected = allTransactions.filter((t: any) => t.status === 'Rejected' || t.checkerDecision === 'rejected');
      const formattedRejected: Transaction[] = rejected.map((t: any) => ({
        id: t.id || '',
        senderName: t.senderName || t.parties?.sender?.name || 'Unknown',
        receiverName: t.receiverName || t.parties?.receiver?.name || 'Unknown',
        amount: t.amount || t.amounts?.sendAmount || 0,
        currency: t.currency || t.amounts?.sendCurrency || 'AED',
        payoutAmount: t.payoutAmount || t.amounts?.payoutAmount || 0,
        payoutCurrency: t.payoutCurrency || t.amounts?.payoutCurrency || 'PKR',
        date: t.date || new Date().toISOString().split('T')[0],
        status: t.status || 'Rejected',
        rejectionReason: t.rejectionReason || t.checkerReason || 'No reason provided',
        createdAt: t.createdAt || new Date().toISOString(),
        branchCode: t.branchCode,
        agentCode: t.agentCode,
        mto: t.agent || t.mto
      }));
      setRejectedTransactions(formattedRejected);
    }
  };
  useEffect(() => {
    loadRejectedTransactions();
    const handleUpdate = () => {
      loadRejectedTransactions();
    };
    window.addEventListener('transactionUpdated', handleUpdate);
    window.addEventListener('transactionAdded', handleUpdate);
    return () => {
      window.removeEventListener('transactionUpdated', handleUpdate);
      window.removeEventListener('transactionAdded', handleUpdate);
    };
  }, []);
  const handleEdit = (transaction: Transaction) => {
    setSelectedTxn(transaction);
    setEditedData({
      senderName: transaction.senderName,
      receiverName: transaction.receiverName,
      amount: transaction.amount,
      payoutAmount: transaction.payoutAmount
    });
    setShowEditDialog(true);
  };
  const handleResubmit = () => {
    if (!selectedTxn) return;
    const stored = localStorage.getItem('initiatedTransactions');
    if (!stored) return;
    const allTransactions = JSON.parse(stored);
    const updatedTransactions = allTransactions.map((t: any) => {
      if (t.id === selectedTxn.id) {
        return {
          ...t,
          ...editedData,
          status: 'Pending Checker Approval',
          checkerDecision: null,
          checkerReason: null,
          rejectionReason: null,
          rejectedAt: null,
          rejectedBy: null,
          resubmittedAt: new Date().toISOString(),
          overview: {
            ...t.overview,
            status: 'Pending Checker Approval',
            amount: editedData.amount
          },
          parties: {
            ...t.parties,
            sender: {
              ...t.parties?.sender,
              name: editedData.senderName
            },
            receiver: {
              ...t.parties?.receiver,
              name: editedData.receiverName
            }
          },
          amounts: {
            ...t.amounts,
            sendAmount: editedData.amount,
            payoutAmount: editedData.payoutAmount
          }
        };
      }
      return t;
    });
    localStorage.setItem('initiatedTransactions', JSON.stringify(updatedTransactions));
    window.dispatchEvent(new Event('transactionUpdated'));
    setShowEditDialog(false);
    setSelectedTxn(null);
  };
  return <Box sx={{
    p: 3,
    bgcolor: 'background.default',
    minHeight: '100vh'
  }} data-id="element-988">
      <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      mb: 3
    }} data-id="element-989">
        <Icon icon="lucide:x-circle" width={28} height={28} color="#AE2A19" data-id="element-990" />
        <Typography variant="h4" sx={{
        fontWeight: 700,
        color: '#AE2A19'
      }} data-id="element-991">
          Rejected Transactions Approval
        </Typography>
      </Box>

      {rejectedTransactions.length === 0 ? <Paper elevation={1} sx={{
      p: 4,
      textAlign: 'center'
    }} data-id="element-992">
          <Icon icon="lucide:check-circle" width={64} height={64} color="#10B981" style={{
        marginBottom: 16
      }} data-id="element-993" />
          <Typography variant="h6" color="text.secondary" gutterBottom data-id="element-994">
            No Rejected Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary" data-id="element-995">
            All your transactions are either approved or pending approval.
          </Typography>
        </Paper> : <Paper elevation={1} data-id="element-996">
          <Box sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'error.50',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }} data-id="element-997">
            <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-998">
              <Icon icon="lucide:check-circle" width={20} height={20} color="#0B7923" data-id="element-999" />
              <Typography variant="h6" color="error.main" data-id="element-1000">
                Transactions Requiring Your Attention
              </Typography>
              <Box sx={{
            bgcolor: 'error.main',
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontSize: '0.875rem',
            fontWeight: 600
          }} data-id="element-1001">
                {rejectedTransactions.length}
              </Box>
            </Box>
          </Box>

          <Alert severity="warning" sx={{
        m: 2
      }} data-id="element-1002">
            These transactions were rejected by the checker. Please review the
            rejection reason, update the necessary information, and resubmit for
            approval.
          </Alert>

          <TableContainer data-id="element-1003">
            <Table data-id="element-1004">
              <TableHead data-id="element-1005">
                <TableRow sx={{
              bgcolor: 'error.100'
            }} data-id="element-1006">
                  <TableCell sx={{
                fontWeight: 600,
                color: 'error.dark'
              }} data-id="element-1007">
                    Transaction ID
                  </TableCell>
                  <TableCell sx={{
                fontWeight: 600,
                color: 'error.dark'
              }} data-id="element-1008">
                    Sender
                  </TableCell>
                  <TableCell sx={{
                fontWeight: 600,
                color: 'error.dark'
              }} data-id="element-1009">
                    Receiver
                  </TableCell>
                  <TableCell sx={{
                fontWeight: 600,
                color: 'error.dark'
              }} data-id="element-1010">
                    Amount
                  </TableCell>
                  <TableCell sx={{
                fontWeight: 600,
                color: 'error.dark'
              }} data-id="element-1011">
                    Rejection Reason
                  </TableCell>
                  <TableCell sx={{
                fontWeight: 600,
                color: 'error.dark'
              }} data-id="element-1012">
                    Rejected At
                  </TableCell>
                  <TableCell sx={{
                fontWeight: 600,
                color: 'error.dark'
              }} data-id="element-1013">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-id="element-1014">
                {rejectedTransactions.map(transaction => <TableRow key={transaction.id} hover data-id="element-1015">
                    <TableCell sx={{
                color: 'primary.main',
                fontWeight: 500
              }} data-id="element-1016">
                      {transaction.id}
                    </TableCell>
                    <TableCell data-id="element-1017">{transaction.senderName}</TableCell>
                    <TableCell data-id="element-1018">{transaction.receiverName}</TableCell>
                    <TableCell data-id="element-1019">
                      {formatCurrency(transaction.amount, transaction.currency)}
                    </TableCell>
                    <TableCell data-id="element-1020">
                      <Typography variant="body2" color="error" sx={{
                  maxWidth: 300
                }} data-id="element-1021">
                        {transaction.rejectionReason}
                      </Typography>
                    </TableCell>
                    <TableCell data-id="element-1022">
                      {transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell data-id="element-1023">
                      <Button size="small" variant="contained" color="primary" startIcon={<EditIcon size={16} data-id="element-1025" />} onClick={() => handleEdit(transaction)} sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }} data-id="element-1024">
                        Update & Resubmit
                      </Button>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>}

      {/* Edit & Resubmit Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="md" fullWidth data-id="element-1026">
        <DialogTitle data-id="element-1027">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }} data-id="element-1028">
            <Typography variant="h6" data-id="element-1029">Update Transaction & Resubmit</Typography>
            <IconButton onClick={() => setShowEditDialog(false)} data-id="element-1030">
              <Icon icon="lucide:x" width={20} height={20} data-id="element-1031" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-1032">
          {selectedTxn && <>
              <Alert severity="error" sx={{
            mb: 3,
            mt: 2
          }} data-id="element-1033">
                <Typography variant="body2" fontWeight={600} gutterBottom data-id="element-1034">
                  Rejection Reason:
                </Typography>
                <Typography variant="body2" data-id="element-1035">
                  {selectedTxn.rejectionReason}
                </Typography>
              </Alert>

              <Grid container spacing={2} data-id="element-1036">
                <Grid item xs={12} md={6} data-id="element-1037">
                  <TextField fullWidth label="Sender Name" value={editedData.senderName} onChange={e => setEditedData({
                ...editedData,
                senderName: e.target.value
              })} data-id="element-1038" />
                </Grid>
                <Grid item xs={12} md={6} data-id="element-1039">
                  <TextField fullWidth label="Receiver Name" value={editedData.receiverName} onChange={e => setEditedData({
                ...editedData,
                receiverName: e.target.value
              })} data-id="element-1040" />
                </Grid>
                <Grid item xs={12} md={6} data-id="element-1041">
                  <TextField fullWidth label="Send Amount" type="number" value={editedData.amount} onChange={e => setEditedData({
                ...editedData,
                amount: parseFloat(e.target.value)
              })} data-id="element-1042" />
                </Grid>
                <Grid item xs={12} md={6} data-id="element-1043">
                  <TextField fullWidth label="Payout Amount" type="number" value={editedData.payoutAmount} onChange={e => setEditedData({
                ...editedData,
                payoutAmount: parseFloat(e.target.value)
              })} data-id="element-1044" />
                </Grid>
              </Grid>
            </>}
        </DialogContent>
        <DialogActions sx={{
        p: 2
      }} data-id="element-1045">
          <Button onClick={() => setShowEditDialog(false)} sx={{
          textTransform: 'none'
        }} data-id="element-1046">
            Cancel
          </Button>
          <Button variant="contained" color="primary" startIcon={<SendIcon size={18} data-id="element-1048" />} onClick={handleResubmit} sx={{
          textTransform: 'none',
          fontWeight: 600
        }} data-id="element-1047">
            Resubmit for Approval
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default RejectedTransactionsApprovalPage;